import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon,
  ZoomOutMap as ResetZoomIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  RotateRight as RotateRightIcon,
  RotateLeft as RotateLeftIcon,
  Info as InfoIcon,
  HelpOutline as HelpIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon
} from '@mui/icons-material';
import { getSignedFileUrl } from '../../services/api';
import ImageViewer from './ImageViewer';

/**
 * Modal component for previewing images with zoom controls
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Object} props.file - File object with key, name, mimetype
 * @param {string} props.title - Optional title for the modal
 */
const ImagePreviewModal = ({ 
  open, 
  onClose, 
  file,
  title
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);
  const imageContainerRef = useRef(null);
  const fullscreenImageRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (open && file?.key) {
      loadImage();
    }
    return () => {
      // Reset state when closing
      setImageUrl('');
      setLoading(true);
      setError(null);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setRotation(0);
      setImageInfo(null);
    };
  }, [open, file]);

  const loadImage = async () => {
    try {
      setLoading(true);
      setError(null);
      // Always load original for full preview modal
      const fileKey = file.key || file;
      // Don't pass version parameter for backward compatibility
      const url = await getSignedFileUrl(fileKey);
      setImageUrl(url);
    } catch (err) {
      console.error('Failed to load image:', err);
      setError('Failed to load image');
    } finally {
      setLoading(false);
    }
  };

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleRotateRight = useCallback(() => {
    setRotation(prev => (prev + 90) % 360);
  }, []);

  const handleRotateLeft = useCallback(() => {
    setRotation(prev => (prev - 90 + 360) % 360);
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      if (fullscreenImageRef.current?.requestFullscreen) {
        fullscreenImageRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else if (fullscreenImageRef.current?.webkitRequestFullscreen) {
        // Safari support
        fullscreenImageRef.current.webkitRequestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      } else if (document.webkitExitFullscreen) {
        // Safari support
        document.webkitExitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement || !!document.webkitFullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      // Prevent default for our shortcuts
      const preventDefault = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', '+', '-', '=', 'r', 'R', 'f', 'F', ' ', 'h', 'H', '?'].includes(e.key);
      if (preventDefault) {
        e.preventDefault();
      }
      
      switch(e.key) {
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
        case '_':
          handleZoomOut();
          break;
        case 'ArrowLeft':
          if (scale > 1) {
            setPosition(prev => ({ ...prev, x: prev.x + 50 }));
          }
          break;
        case 'ArrowRight':
          if (scale > 1) {
            setPosition(prev => ({ ...prev, x: prev.x - 50 }));
          }
          break;
        case 'ArrowUp':
          if (scale > 1) {
            setPosition(prev => ({ ...prev, y: prev.y + 50 }));
          }
          break;
        case 'ArrowDown':
          if (scale > 1) {
            setPosition(prev => ({ ...prev, y: prev.y - 50 }));
          }
          break;
        case 'r':
        case 'R':
          if (e.shiftKey) {
            handleRotateLeft();
          } else {
            handleRotateRight();
          }
          break;
        case 'f':
        case 'F':
          handleToggleFullscreen();
          break;
        case ' ':
          handleResetZoom();
          setRotation(0);
          break;
        case 'h':
        case 'H':
        case '?':
          setShowHelp(prev => !prev);
          break;
        case 'Escape':
          if (isFullscreen) {
            handleResetZoom();
          }
          break;
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, scale, isFullscreen, handleZoomIn, handleZoomOut, handleResetZoom, handleRotateLeft, handleRotateRight, handleToggleFullscreen]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(prev => Math.max(0.5, Math.min(3, prev + delta)));
  }, []);

  // Load image info
  useEffect(() => {
    if (imageUrl && imageRef.current) {
      const img = imageRef.current;
      img.onload = () => {
        setImageInfo({
          width: img.naturalWidth,
          height: img.naturalHeight,
          size: file?.size || file?.filesize,
          type: file?.mimetype || file?.type || 'image'
        });
      };
    }
  }, [imageUrl, file]);

  const handleDownload = async () => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  // Mouse event handlers for dragging
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (scale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
      e.preventDefault();
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && scale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const fileName = file?.originalname || file?.name || 'Image';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          height: { xs: '90vh', sm: '80vh', md: '85vh' },
          m: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
          {title || fileName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleZoomOut}
            disabled={scale <= 0.5}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <ZoomOutIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: '3em', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          <IconButton
            onClick={handleZoomIn}
            disabled={scale >= 3}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <ZoomInIcon />
          </IconButton>
          <IconButton
            onClick={handleRotateLeft}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Rotate left (Shift+R)"
          >
            <RotateLeftIcon />
          </IconButton>
          <IconButton
            onClick={handleRotateRight}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Rotate right (R)"
          >
            <RotateRightIcon />
          </IconButton>
          <IconButton
            onClick={handleResetZoom}
            disabled={scale === 1 && position.x === 0 && position.y === 0 && rotation === 0}
            size="small"
            sx={{ color: 'text.secondary' }}
            title="Reset view (Space)"
          >
            <ResetZoomIcon />
          </IconButton>
          <IconButton
            onClick={handleToggleFullscreen}
            size="small"
            sx={{ color: 'text.secondary', ml: 1 }}
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton
            onClick={handleDownload}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={() => setShowHelp(!showHelp)}
            size="small"
            sx={{ 
              color: showHelp ? 'primary.main' : 'text.secondary',
              bgcolor: showHelp ? 'action.selected' : 'transparent'
            }}
            title="Keyboard shortcuts (H)"
          >
            <HelpIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: 'text.secondary', ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'grey.100',
          flex: 1,
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Keyboard shortcuts help */}
        {showHelp && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 1000,
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 2,
              boxShadow: theme.shadows[4],
              maxWidth: 320
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Keyboard Shortcuts
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, fontSize: '0.875rem' }}>
              <Typography variant="body2" color="text.secondary">+/-</Typography>
              <Typography variant="body2">Zoom in/out</Typography>
              
              <Typography variant="body2" color="text.secondary">Mouse wheel</Typography>
              <Typography variant="body2">Zoom in/out</Typography>
              
              <Typography variant="body2" color="text.secondary">Double click</Typography>
              <Typography variant="body2">Toggle 100% zoom</Typography>
              
              <Typography variant="body2" color="text.secondary">Arrow keys</Typography>
              <Typography variant="body2">Pan image (when zoomed)</Typography>
              
              <Typography variant="body2" color="text.secondary">R / Shift+R</Typography>
              <Typography variant="body2">Rotate right/left</Typography>
              
              <Typography variant="body2" color="text.secondary">Space</Typography>
              <Typography variant="body2">Reset view</Typography>
              
              <Typography variant="body2" color="text.secondary">F</Typography>
              <Typography variant="body2">Toggle fullscreen</Typography>
              
              <Typography variant="body2" color="text.secondary">H / ?</Typography>
              <Typography variant="body2">Show/hide this help</Typography>
            </Box>
          </Box>
        )}

        {/* Image info */}
        {imageInfo && !loading && !error && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              zIndex: 999,
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              boxShadow: theme.shadows[2],
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              opacity: 0.9
            }}
          >
            <InfoIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {imageInfo.width} × {imageInfo.height} px
              {imageInfo.size && ` • ${(imageInfo.size / 1024).toFixed(1)} KB`}
              {imageInfo.type && ` • ${imageInfo.type.split('/').pop()?.toUpperCase()}`}
            </Typography>
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading image...
            </Typography>
          </Box>
        )}
        
        {error && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              p: 4
            }}
          >
            <Typography color="error" variant="h6">
              {error}
            </Typography>
          </Box>
        )}
        
        {!loading && !error && imageUrl && (
          <Box
            ref={fullscreenImageRef}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              overflow: 'auto',
              position: 'relative',
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              userSelect: 'none',
              bgcolor: isFullscreen ? 'black' : 'transparent',
              '&:fullscreen': {
                bgcolor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              },
              '&::-webkit-full-screen': {
                bgcolor: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}
            onDoubleClick={handleDoubleClick}
          >
            <Box
              ref={imageContainerRef}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <img
                ref={imageRef}
                src={imageUrl}
                alt={fileName}
                style={{
                  width: scale === 1 ? 'auto' : `${scale * 100}%`,
                  height: scale === 1 ? 'auto' : `${scale * 100}%`,
                  maxWidth: scale === 1 ? '100%' : 'none',
                  maxHeight: scale === 1 ? '100%' : 'none',
                  objectFit: 'contain',
                  transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                  pointerEvents: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none'
                }}
                onError={() => setError('Failed to display image')}
                draggable={false}
              />
              {isFullscreen && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    display: 'flex',
                    gap: 1.5,
                    zIndex: 1000,
                    bgcolor: theme.palette.background.paper,
                    borderRadius: 2,
                    p: 1,
                    boxShadow: theme.shadows[8],
                    border: `1px solid ${theme.palette.divider}`
                  }}
                >
                  <IconButton
                    onClick={handleZoomOut}
                    disabled={scale <= 0.5}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.primary.main,
                      '&:hover': { 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText 
                      },
                      '&.Mui-disabled': { 
                        bgcolor: 'transparent',
                        color: theme.palette.action.disabled 
                      }
                    }}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 1,
                      minWidth: '80px',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.primary 
                      }}
                    >
                      {Math.round(scale * 100)}%
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleZoomIn}
                    disabled={scale >= 3}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.primary.main,
                      '&:hover': { 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText 
                      },
                      '&.Mui-disabled': { 
                        bgcolor: 'transparent',
                        color: theme.palette.action.disabled 
                      }
                    }}
                  >
                    <ZoomInIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleRotateLeft}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.secondary.main,
                      '&:hover': { 
                        bgcolor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText 
                      }
                    }}
                    title="Rotate left"
                  >
                    <RotateLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleRotateRight}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.secondary.main,
                      '&:hover': { 
                        bgcolor: theme.palette.secondary.main,
                        color: theme.palette.secondary.contrastText 
                      }
                    }}
                    title="Rotate right"
                  >
                    <RotateRightIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleResetZoom}
                    disabled={scale === 1 && position.x === 0 && position.y === 0 && rotation === 0}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.info.main,
                      '&:hover': { 
                        bgcolor: theme.palette.info.main,
                        color: theme.palette.info.contrastText 
                      },
                      '&.Mui-disabled': { 
                        bgcolor: 'transparent',
                        color: theme.palette.action.disabled 
                      }
                    }}
                    title="Reset view"
                  >
                    <ResetZoomIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleToggleFullscreen}
                    size="medium"
                    sx={{
                      bgcolor: theme.palette.action.hover,
                      color: theme.palette.error.main,
                      '&:hover': { 
                        bgcolor: theme.palette.error.main,
                        color: theme.palette.error.contrastText 
                      }
                    }}
                    title="Exit fullscreen"
                  >
                    <FullscreenExitIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;